import type { ContentPage } from "../types";

// Jammable facts are from the code: sort tabs and category filters from
// pages/models.tsx and ModelSearchPageV2 (i18n messages/library.ts), voice
// page demos and "Other versions" from messages/voice.ts, up to 6 voices a
// cover (MAX_VOICES in components/modals/conversionRequest.js), training
// limits from lib/train/clips.ts, times from lib/train/eta.ts (medians of
// 2,103 voices, Sep-Oct 2026), 300 epochs from the training payload in
// api/model/startTrainingFull. Re-check them when those files change.
const page: ContentPage = {
  section: "blog",
  slug: "ai-voice-models",
  coverLabel: "Voice models",
  title: "AI Voice Models: What They Are, Where to Find and Use Them",
  metaDescription:
    "What an AI voice model is, the kinds there are, what makes a good one, and how to find, use and make your own on Jammable, with 60,000+ voices to try.",
  h1: "AI voice models: what they are and how to use them",
  kind: "Guide",
  stats: [
    { value: "60,000+", label: "community voice models on Jammable" },
    { value: "6", label: "voices you can mix in one cover" },
    { value: "3 min", label: "of audio is enough to train your own" },
  ],
  answer:
    "An AI voice model is a file trained on recordings of one voice so it can re-sing or re-speak other audio in that voice. Jammable has more than 60,000 community voice models you can search by name or category and use for covers, duets and text-to-speech, and you can train your own from 3 minutes of audio.",
  takeaways: [
    "A voice model learns how a voice sounds, not songs or lyrics: the performance always comes from your input.",
    "There are three kinds: community voices, licensed voices on some paid tools, and voices you train yourself.",
    "Clean training audio matters more than anything else in how good a model sounds.",
    "On Jammable, search by name, filter by category and sort by Trending, Most used, Most liked or Newest, then listen to the demos before you pick.",
  ],
  queries: [
    "voice models",
    "ai voice model",
    "ai voice models",
    "where to find ai voice models",
    "how to use ai voice models",
  ],
  published: "2026-10-17",
  updated: "2026-10-17",
  blocks: [
    {
      type: "md",
      text: `## What is an AI voice model?

An AI voice model is a small AI file that has learned what one voice sounds like: its tone, texture, the way it moves between notes. Give it a vocal recording and it performs the same words and melody again in its own voice. Give it speech and it speaks in that voice.

It does not learn songs, lyrics or a style of performance. Those always come from what you put in. That is why one voice model can sing a ballad, rap or read a paragraph: it supplies the voice, you supply the performance.

Almost every voice model used for AI covers, including the ones on Jammable, is an **RVC model** (Retrieval-based Voice Conversion). On disk that is a .pth file, the trained voice, and usually an .index file that helps the result sound closer to the original voice. [What is RVC?](/blog/what-is-rvc) explains how the two files work.

## Kinds of AI voice models

Voice models differ mostly in who made them and what you are allowed to do with them.`,
    },
    {
      type: "table",
      heading: "The kinds of AI voice models",
      table: {
        columns: ["Kind", "Who makes it", "Where you find it", "Good for"],
        rows: [
          [
            "Community voices",
            "Users, who train and share voices with everyone",
            "Large public libraries such as Jammable's, with 60,000+ voices",
            "The widest choice: singers' styles, anime and cartoon voices, gaming voices, narrators",
          ],
          [
            "Licensed voices",
            "A company working with the voice's owner",
            "Some paid tools; Kits.ai says its library voices are licensed for commercial use",
            "Commercial work that calls for a voice licensed by its owner",
          ],
          [
            "Your own voice",
            "You, from recordings of yourself",
            "Your Library, private or public",
            "Hearing yourself in other songs, styles or harmonies, and text-to-speech in your voice",
          ],
          [
            "Uploaded RVC models",
            "You, trained elsewhere",
            "Uploaded to your Jammable Library as a .pth (and .index)",
            "Keeping voices you trained in other tools or on sites that closed",
          ],
        ],
      },
    },
    {
      type: "md",
      text: `Train or upload only your own voice, or a voice whose owner has agreed to it.

## What makes a good AI voice model?

Two voice models of the same voice can sound very different. The difference is almost always the training audio.

- **Enough audio.** Jammable needs at least 3 minutes of singing or speech, after silence is cut, and accepts up to 60. Voices usually sound better with 6 minutes or more. RVC's own FAQ recommends 10 to 50 minutes for its local trainer.
- **Clean audio.** One voice, no music, no backing vocals, no echo. A model trained on noisy audio learns the noise as part of the voice.
- **Range.** Low and high notes, quiet and loud, sung and spoken. A model only sounds natural in the range it heard.
- **The right amount of training.** Training runs through the audio many times; each pass is an epoch. Jammable's training jobs are set to 300 epochs, so you don't choose. The RVC FAQ notes that 20 to 30 epochs are enough for poor audio and that more epochs won't fix a bad dataset.
- **An index file.** A model with its .index usually sounds closer to the target voice, especially when the training audio was short.

You can hear most of this without any files. A good model sounds natural on high notes and long vowels; a weak one turns robotic, hissy or breaks up when the input goes outside the range it learned.`,
    },
    {
      type: "steps",
      heading: "How to find an AI voice model on Jammable",
      items: [
        {
          title: "Open the voice library",
          body: "Go to [Voices](/models). It shows 60,000+ community voices, and new ones arrive every day.",
        },
        {
          title: "Search or filter",
          body: "Search by name, or pick a category such as [anime](/models?filter=anime), [cartoon](/models?filter=cartoon), [gaming](/models?filter=gaming) or [music](/models?filter=music). Collections group voices that go together.",
        },
        {
          title: "Sort the results",
          body: "Choose *Trending today* to see what people are using right now, *Most used* or *Most liked* for proven voices, or *Newest* for the latest uploads.",
        },
        {
          title: "Listen before you choose",
          body: "Voice pages have auto-generated demos of the voice singing and speaking, shows how many times the voice has been used, and lists other versions of the same voice and similar voices.",
        },
        {
          title: "Save the ones you like",
          body: "Like a voice or save it to a collection so you can find it again from your [Library](/library).",
        },
      ],
    },
    {
      type: "callout",
      tone: "tip",
      title: "Compare versions of the same voice",
      text: "Popular voices often have several community versions trained on different audio. Check *Other versions* on the voice page and play the demos side by side: the one with the most uses is not always the one that suits your song.",
    },
    {
      type: "md",
      text: `## How to use an AI voice model

Once you have picked a voice, there are three main things to do with it on Jammable.

- **Covers.** Add a recording of your own, or audio you have the rights to, and Jammable separates the vocal, converts it into the voice and mixes it back over the music. A cover is usually ready in about a minute. See [AI singing voice generator](/blog/ai-singing-voice-generator) for more on singing.
- **Duets and groups.** Put up to 6 voices in one cover. Jammable decides who sings when, or you can choose which voice sings each part in the [studio](/create).
- **Text-to-speech.** Type what you want said, choose an accent, expression and speed, and hear it in the voice. Jammable speaks your text first and then converts it with the voice model.

You can also use a voice as a voice changer for recordings: upload or record your own audio and convert it with the [AI voice changer](/ai-voice-changer). Jammable does not change your voice live, so it is not for calls or streams.

Making covers needs a plan, from $1.99 for the first month and then $8.99 a month, with unlimited covers on every plan. Browsing voices and listening to demos is free.

## Settings that make a voice model sound right

- **Pitch.** Set in semitones, from -24 to +24. Going from a lower voice to a higher one? Try +12. From a higher one to a lower one, -12. On a single-voice cover, a shift that isn't a full octave moves the music by the same amount so the vocal stays in key.
- **Split out backing vocals** when a song has harmonies or ad-libs, for a cleaner result.
- **My audio is just vocals** skips separating the vocal from the music when your file is already a vocal on its own.
- **Autotune** snaps the vocal to the key, from a light polish to the obvious robotic sound, and **vocal volume** sets the vocal louder or quieter than the music.`,
    },
    {
      type: "matrix",
      heading: "Community voice, your own voice or an uploaded model?",
      matrix: {
        caption: "What each kind of voice model takes on Jammable",
        tools: ["Community voice", "Train your own", "Upload an RVC model"],
        rows: [
          {
            feature: "Ready to use",
            values: [
              "Right away",
              "Usually in 25 min to 2 hours",
              "Right away",
            ],
          },
          {
            feature: "Plan needed",
            values: [
              "Any plan",
              "Creator or Power, or a training pass",
              "Creator or Power",
            ],
          },
          { feature: "Covers and duets", values: ["yes", "yes", "yes"] },
          { feature: "Text-to-speech", values: ["yes", "yes", "yes"] },
          { feature: "Can be kept private", values: ["no", "yes", "yes"] },
          {
            feature: "Files needed",
            values: ["None", "3 to 60 min of audio", ".pth, plus .index"],
          },
        ],
      },
    },
    {
      type: "md",
      text: `## How to make your own AI voice model

If the voice you want isn't in the library, or you want to hear yourself, train a voice model. On Jammable it takes a few steps in the browser, with nothing to install and no graphics card:

1. Upload MP3, WAV or M4A files, or record straight into the page on [Train](/train).
2. Check what is kept: silence is cut automatically, and you can trim coughs or noise.
3. Name the voice, add a picture and choose *Private, only me* or *Public in the library*.
4. Turn on *Remove instrumentals* or *Remove reverb* if the recordings need it, then start training. You get an email when it is ready.

The full walkthrough, with recording tips, is in [how to train your own AI voice model](/blog/how-to-train-an-ai-voice-model). If you already have an RVC model you trained somewhere else, upload the .pth and .index files from your [Library](/library) instead.`,
    },
    {
      type: "bars",
      heading: "How long a new voice model takes to train",
      caption:
        "Median time from submit to ready on Jammable, by minutes of audio after silence is cut (2,103 voices, Sep to Oct 2026)",
      items: [
        { label: "Up to 10 min", value: 23, display: "about 25 min" },
        { label: "10 to 20 min", value: 37, display: "about 35 min" },
        { label: "20 to 30 min", value: 53, display: "about 55 min" },
        { label: "30 to 45 min", value: 71, display: "about 1 h 10 min" },
        { label: "45 to 60 min", value: 107, display: "about 1 h 45 min" },
      ],
    },
    {
      type: "cta",
      href: "/models",
      label: "Find a voice",
      text: "Search 60,000+ AI voice models and hear the demos for free.",
    },
  ],
  faqs: [
    {
      q: "What are AI voice models?",
      a: "AI voice models are AI files trained on recordings of one voice. They can re-sing or re-speak other audio in that voice while keeping its words, melody and timing. Most voice models used for covers are RVC models, a .pth file plus an optional .index file.",
    },
    {
      q: "Where can I find AI voice models?",
      a: "Jammable's voice library has more than 60,000 community voice models. Search by name, filter by categories like anime, cartoon, gaming and music, and sort by Trending today, Most used, Most liked or Newest. Each voice page has demos you can play first.",
    },
    {
      q: "How do I use an AI voice model?",
      a: "Pick a voice, then add your own recording or audio you have the rights to. Jammable separates the vocal, converts it and mixes it back, usually in about a minute. You can also put up to 6 voices in one cover or use the voice for text-to-speech.",
    },
    {
      q: "Are AI voice models free?",
      a: "Browsing Jammable's voice models and listening to their demos is free. Making covers or text-to-speech with them needs a plan, from $1.99 for the first month, and every plan has unlimited covers.",
    },
    {
      q: "Can I make an AI voice model of my own voice?",
      a: "Yes. Upload or record at least 3 minutes of your singing or speech on Jammable's Train page; 6 minutes or more usually sounds better. A voice trained on up to 10 minutes of audio is usually ready in about 25 minutes, and you can keep it private.",
    },
    {
      q: "Can I use an AI voice model as a live voice changer?",
      a: "Not on Jammable. Jammable converts recordings and text, so it suits covers, voiceovers and clips, not live calls or streams. For live use you need a desktop real-time voice changer.",
    },
  ],
  related: [
    { label: "Browse 60,000+ AI voices", href: "/models" },
    { label: "What is RVC?", href: "/blog/what-is-rvc" },
    {
      label: "Train your own voice",
      href: "/blog/how-to-train-an-ai-voice-model",
    },
    { label: "AI voice cloning", href: "/blog/ai-voice-cloning" },
    { label: "AI character voices", href: "/blog/ai-character-voices" },
    { label: "Pricing", href: "/pricing" },
  ],
  sources: [
    { label: "Kits.ai voices", href: "https://www.kits.ai/ai-voice-changers" },
    {
      label: "RVC FAQ (English)",
      href: "https://github.com/RVC-Project/Retrieval-based-Voice-Conversion-WebUI/blob/main/docs/en/faq_en.md",
    },
  ],
};

export default page;
